import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';

import {PMEIProcedureQuery} from './__generated/PMEIProcedureQuery';
import {getConditionReplicateText, getConditionsLabel} from './WorksheetUtils';

type ProcedureMethodExecutionInstanceNode = ExtractByTypename<
  PMEIProcedureQuery['procedureMethodExecutionInstance'],
  'ProcedureMethodExecutionInstanceNode'
>['procedureRun']['methodExecutionInstances'][number];

export interface WorksheetConditionsLabelProps {
  worksheetId: string;
  procedureType: ProcedureType;
  studyType: StudyType;
  methodExecutionInstances: Array<ProcedureMethodExecutionInstanceNode>;
}

export default function WorksheetConditionsLabel(props: WorksheetConditionsLabelProps): JSX.Element | null {
  if (!getTypedStudyAdapter(props.studyType).isMultiConditionEnabled) {
    return null;
  }
  assert(
    props.methodExecutionInstances.length > 0,
    'Expected all procedure runs to have at least one method execution instance'
  );
  const livePMEIsForWorksheet = props.methodExecutionInstances.filter(
    (pmei) =>
      !isInaccessible(pmei.worksheet) && pmei.worksheet.id === props.worksheetId && !pmei.archiveData.archived
  );

  return (
    <Popover
      interaction={FloatingInteraction.CLICK}
      placement={FloatingPlacement.BOTTOM}
      renderContent={() => (
        <div className={css(styles.conditionsPopoverContainer)}>
          {livePMEIsForWorksheet.map((pmei) => (
            <div key={pmei.id} className={css(styles.conditionsPopoverItem)}>
              {getConditionReplicateText(pmei)}
            </div>
          ))}
        </div>
      )}
    >
      <div className={css(styles.conditionsLabel)}>
        {getConditionsLabel(props.procedureType, livePMEIsForWorksheet)}
      </div>
    </Popover>
  );
}

export const styles = StyleSheet.create({
  conditionsLabel: {
    background: Color.GRAY_100,
    color: Color.GRAY_800,
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: '8px',
    padding: '4px 8px',
    width: 'fit-content',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      background: Color.GRAY_200,
      cursor: 'pointer',
    },
    ':active': {
      background: Color.GRAY_300,
    },
  },
  conditionsPopoverContainer: {
    width: '200px',
    maxHeight: '400px',
  },
  conditionsPopoverItem: {
    fontSize: '12px',
    lineHeight: '14px',
    marginBottom: '4px',
  },
});
